import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { Button } from "@/components/ui/button-tw";
import { useGetMRDC } from "@/hooks/mrdc/use-get-mrdc";
import { useSubmitMRDC } from "@/hooks/mrdc/use-submit-mrdc";
import { useUpdateMRDCMobile } from "@/hooks/mrdc/use-update-mrdc-mobile";
import { useUploadCheck } from "@/hooks/mrdc/use-upload-check";
import {
  ArrowRight,
  CameraIcon,
  Check,
  CheckCircle,
  Loader2,
  Squircle,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera } from "react-camera-pro";
import {
  deviceType,
  mobileModel,
  mobileVendor,
  osName,
  osVersion,
} from "react-device-detect";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { convertThousandSeparator } from "@/scripts/functions";
import { cn } from "@/utils/classnames";

export function MRDC() {
  const { id: linkId } = useParams();

  const { mutate: updateMRDC, isLoading: updatingMRDC } = useUpdateMRDCMobile();
  const { mutate: uploadCheck, isLoading: uploadingCheck } = useUploadCheck();
  const [mrdcId, setMrdcId] = useState(null);
  const [mrdcAmount, setMrdcAmount] = useState(null);
  const [isTakingPicture, setIsTakingPicture] = useState(false);
  const [side, setSide] = useState(null);
  const [front, setFront] = useState(false);
  const [back, setBack] = useState(false);
  const [step, setStep] = useState("capture");
  const [expired, setExpired] = useState(false);

  const getIp = async () => {
    const res = await axios.get("https://api.ipify.org/?format=json");
    return res.data.ip;
  };

  useEffect(() => {
    const currDate = new Date().toISOString();

    getIp()
      .then((ip) => {
        const deviceInfo = {
          linkId,
          payload: {
            device_info: {
              device_id: `${mobileVendor}-${mobileModel}-${osName}-${osVersion}-${ip}`,
              device_name: mobileVendor,
              device_type: deviceType,
              device_model: mobileModel || "unknown",
              device_os_name: osName,
              device_os_version: osVersion,
              device_ip_address: ip,
              device_created_date: currDate,
              device_updated_date: currDate,
            },
          },
        };

        updateMRDC(deviceInfo, {
          onSuccess: ({ mrdc_id, amount }) => {
            setMrdcId(mrdc_id);
            setMrdcAmount(amount);
          },
          onError: (error) => {
            if (error?.response?.status === 410) {
              setExpired(true);
            }

            // Screen for the case that call failed and we don't have mrdc id?
          },
        });
      })
      .catch((error) => {
        console.log("ERROR GETTING IP::", error);
      });
  }, [linkId]);

  const handleImageConfirm = (imageBlob) => {
    if (!mrdcId) {
      console.log("MRDC ID IS NULL!");
      return;
    }
    setIsTakingPicture(null);
    uploadCheck(
      {
        mrdcId,
        linkId,
        checkSide: side,
        image: imageBlob,
      },
      {
        onSuccess: (data) => {
          const imageURL = data?.url?.key;
          const payload =
            side === "front"
              ? {
                  front_image_file_id: imageURL,
                }
              : {
                  back_image_file_id: imageURL,
                };

          updateMRDC(
            {
              linkId,
              imageType: side,
              payload,
            },
            {
              onSuccess: (data) => {
                if (!data?.image_visible) {
                  toast.error(
                    "Failed validation. Please make sure the image is inside the frame and is of good quality.",
                    {
                      autoClose: 7000,
                    }
                  );
                } else {
                  if (side === "front") {
                    setFront(imageURL);
                  }

                  if (side === "back") {
                    setBack(imageURL);
                  }

                  toast.success("Image uploaded successfully!");
                }
              },
              onError: (error) => {
                if (error?.response?.status === 410) {
                  setExpired(true);
                  return;
                }

                toast.error(
                  "Uh oh. Something went wrong while uploading the image. Please try again.",
                  {
                    autoClose: 7000,
                  }
                );
              },
              onSettled: () => setSide(null),
            }
          );
        },
        onError: (error) => {
          if (error?.response?.status === 410) {
            setExpired(true);
            return;
          }

          toast.error(
            "Uh oh. Something went wrong while uploading the image. Please try again.",
            {
              autoClose: 7000,
            }
          );
          setSide(null);
        },
        onSettled: () => setSide(null),
      }
    );
  };

  if (expired) {
    //validateError: Should now display when update device returns no mrdc id or validation failed
    return (
      <div className="w-screen h-screen">
        <div className="fixed top-0 left-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block"></div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="size-6 text-red-600"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold text-gray-900">
                    Uh oh!
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Your link has expired. Please return to your desktop for a
                      new link to deposit your check.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "submitted") {
    return <UploadComplete />;
  }

  if (step === "review") {
    return (
      <ReviewDetails
        setStep={setStep}
        linkId={linkId}
        amount={mrdcAmount}
        onError={(error) => {
          if (error?.response?.status === 410) {
            setExpired(true);
          }
        }}
      />
    );
  }

  if (step === "capture" && isTakingPicture) {
    return <CustomWebcam side={side} onConfirm={handleImageConfirm} />;
  }

  return (
    <div className="h-screen w-screen p-4">
      <div className="border-b border-gray-200 pb-5">
        <div className="sm:flex sm:items-baseline sm:justify-between">
          <div className="sm:w-0 sm:flex-1">
            <h1
              id="message-heading"
              className="text-base font-semibold text-gray-900"
            >
              Upload check
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Ensure all four corners are showing and place the check on a dark
              background
            </p>
          </div>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-6">
        <div
          className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6 hover:cursor-pointer"
          onClick={() => {
            if (side === "front" && (uploadingCheck || updatingMRDC)) return;

            setIsTakingPicture(true);
            setSide("front");
          }}
        >
          {side === "front" && (uploadingCheck || updatingMRDC) && (
            <div className="absolute top-0 left-0 right-0 bottom-0 z-50 bg-white/60 flex justify-center items-center">
              <Loader2 className="h-10 w-10 animate-spin text-yellow-600" />
            </div>
          )}
          <dt className="flex items-center gap-x-3">
            <div
              className={cn("rounded-md bg-indigo-500 p-3", {
                "bg-green-600": front,
              })}
            >
              {front ? (
                <Check className="h-6 w-6 text-white" />
              ) : (
                <CameraIcon className="h-6 w-6 text-white" />
              )}
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              Front of check
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
              <div className="text-sm">
                <button
                  type="button"
                  className="font-medium text-indigo-600 hover:text-indigo-500 flex items-center"
                >
                  {front ? "Retake" : "Take"} picture{" "}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </dd>
        </div>

        <div
          className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6 hover:cursor-pointer"
          onClick={() => {
            if (side === "back" && (uploadingCheck || updatingMRDC)) return;

            setIsTakingPicture(true);
            setSide("back");
          }}
        >
          {side === "back" && (uploadingCheck || updatingMRDC) && (
            <div className="absolute top-0 left-0 right-0 bottom-0 z-50 bg-white/60 flex justify-center items-center">
              <Loader2 className="h-10 w-10 animate-spin text-yellow-600" />
            </div>
          )}
          <dt className="flex items-center gap-x-3">
            <div
              className={cn("rounded-md bg-indigo-500 p-3", {
                "bg-green-600": back,
              })}
            >
              {back ? (
                <Check className="h-6 w-6 text-white" />
              ) : (
                <CameraIcon className="h-6 w-6 text-white" />
              )}
            </div>
            <p className="text-2xl font-semibold text-gray-900">
              Back of check
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
            <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
              <div className="text-sm">
                <button
                  type="button"
                  className="font-medium text-indigo-600 hover:text-indigo-500 flex items-center"
                >
                  {back ? "Retake" : "Take"} picture{" "}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </dd>
        </div>
      </dl>
      <div className="space-y-4">
        <p
          id="message-heading"
          className="text-base font-semibold text-gray-900"
        >
          Don't forget! Endorse the back of your check as follows:
        </p>
        <div className="text-center space-y-3 border border-border p-3 rounded-md">
          <p>{"[Your Signature]"}</p>
          <p className="mt-1 text-sm text-gray-500 ">
            {`”For Remote Deposit Only” or check the remote deposit box (if there is one) on the back of the check.
          `}
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          onClick={() => {
            setStep("review");
          }}
          disabled={!front || !back}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

function CustomWebcam({ onConfirm, side }) {
  const camera = useRef(null);
  const [image, setImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  // const fullScreenHandle = useFullScreenHandle();

  // useEffect(() => {
  //   fullScreenHandle.enter();
  // }, []);

  const capture = useCallback(() => {
    try {
      const photo = camera.current.takePhoto();
      setImage(photo);
    } catch (error) {
      setCameraError(error.message);
    }
  }, [camera]);

  if (cameraError) {
    return (
      <div className="w-screen h-screen">
        <div className="fixed top-0 left-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <X aria-hidden="true" className="size-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                  <ExclamationTriangleIcon
                    aria-hidden="true"
                    className="size-6 text-red-600"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-base font-semibold text-gray-900">
                    An error has occurred
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{cameraError}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    // <FullScreen handle={fullScreenHandle}>
    <div className="w-screen h-screen">
      {image ? (
        <div className="relative w-screen h-screen">
          <img
            src={image}
            alt="webcam"
            className=" object-cover w-full h-full"
          />
          <div className="absolute bottom-20 left-0 right-0 flex justify-around p-6 ">
            <button
              type="button"
              className=" bg-red-600/10 px-4 py-4 rounded-full  border-4 border-red-600"
              onClick={() => setImage(null)}
            >
              <X className="text-red-600 w-10 h-10" />
            </button>
            <button
              type="button"
              className=" bg-green-600/10 px-4 py-4 rounded-full border-4 border-green-600"
              onClick={() => {
                onConfirm(image);
              }}
            >
              <Check className="text-green-600 w-10 h-10" />
            </button>
          </div>
        </div>
      ) : (
        <div id="camera" className="w-screen h-screen relative">
          <Camera
            ref={camera}
            facingMode="environment"
            errorMessages={{
              noCameraAccessible:
                "No camera device accessible. Please refresh and give camera permission or try a different browser.",
              permissionDenied:
                "Permission denied. Please refresh and give camera permission.",
              switchCamera:
                "It is not possible to switch camera to different one because there is only one video device accessible.",
              canvas: "Canvas is not supported.",
            }}
          />
          <div className="absolute inset-0 flex flex-col border-x-[30px] border-t-[60px] border-b-[120px] border-black/55">
            <div className="h-full border-2 border-white rounded-lg flex justify-center items-end">
              <p className="absolute -right-[198px] top-1/2 text-white font-semibold text-lg rotate-90">
                Capture the <span>{side}</span> of the check in the frame
              </p>

              {side === "back" && (
                <div className="absolute top-0 left-0 right-0 px-8">
                  <div className="border-t-2 border-white mt-8" />
                  <div className="border-t-2 border-white mt-8" />
                  <div className="border-t-2 border-white mt-8" />
                </div>
              )}

              {side === "front" && (
                <div className="absolute top-0 left-0 flex flex-col  p-4">
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                </div>
              )}

              {side === "front" && (
                <div className="absolute bottom-0 right-0 flex flex-col  p-4">
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                  <div className="font-bold text-white">
                    <Squircle />
                  </div>
                </div>
              )}

              <div className="border-4 border-white rounded-full w-16 h-16 flex justify-center items-center mb-3">
                <button
                  type="button"
                  className="rounded-full bg-gray-50/25 w-12 h-12 flex justify-center items-center"
                  onClick={capture}
                >
                  <CameraIcon className="h-6 w-6 text-white " />
                </button>
              </div>
              {/* <button
                type="button"
                className="rounded-full  w-full h-10 m-2  ring-2 ring-ring ring-offset-2 outline-none flex justify-center items-center"
                onClick={capture}
              >
                <CameraIcon className="h-6 w-6 text-white" />
              </button> */}
            </div>
            <div className="absolute h-10 bg-yellow-600"></div>
          </div>

          {/* <div className="z-50 absolute top-7 left-7 w-10 h-10 border-t-4 border-l-4 border-white rounded-ss-lg" />
          <div className="z-50 absolute top-7 right-7 w-10 h-10 border-t-4 border-r-4 border-white rounded-se-lg" />
          <div className="z-50 absolute bottom-7 left-7 w-10 h-10 border-b-4 border-l-4 border-white rounded-es-lg" />
          <div className="z-50 absolute bottom-7 right-7 w-10 h-10 border-b-4 border-r-4 border-white rounded-ee-lg" /> */}
        </div>
      )}
    </div>
    // </FullScreen>
  );
}

function ReviewDetails({ setStep, linkId, amount, onError }) {
  const { mutate: submitDeposit, isLoading: depositing } = useSubmitMRDC();

  return (
    <div className="h-screen w-screen p-4">
      <div className="border-b border-gray-200 pb-5">
        <div className="sm:flex sm:items-baseline sm:justify-between">
          <div className="sm:w-0 sm:flex-1">
            <h1
              id="message-heading"
              className="text-base font-semibold text-gray-900"
            >
              Submit your deposit
            </h1>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 pb-6">
        <dl className="flex flex-wrap">
          <div className="flex-auto pl-6 pt-6">
            <dd className="mt-1 text-lg font-semibold leading-6 text-gray-900">
              ${convertThousandSeparator(amount)}
            </dd>
          </div>
        </dl>
      </div>

      <p className="text-sm font-medium text-gray-900 mt-6">
        The daily and single deposit limit is $75,000. To learn more about your
        limits or request an increase please{" "}
        <a
          href="https://pocketbookhelp.zendesk.com/hc/en-us"
          target="_blank"
          className="border-b border-gray-900 hover:text-blue-900/10 "
        >
          contact us
        </a>
        .
      </p>

      <div className="flex justify-between mt-6 gap-x-4">
        <Button
          variant="outline"
          onClick={() => setStep("capture")}
          className="w-1/2"
        >
          Go Back
        </Button>
        <Button
          onClick={() => {
            submitDeposit(
              { linkId },
              {
                onSuccess: () => {
                  toast.success("Your deposit was submitted!");
                  setStep("submitted");
                },
                onError: (error) => {
                  onError(error);
                  toast.error(
                    error?.response?.data?.details ||
                      "An error occurred while submitting deposit.",
                    {
                      autoClose: 10000,
                    }
                  );
                },
              }
            );
          }}
          className="w-1/2"
        >
          Submit Deposit{" "}
          {depositing && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
        </Button>
      </div>
      <p className="text-sm font-medium text-gray-900 mt-6">
        Deposits submitted by 4:00 PM EST on a business day will be processed
        the same day. Deposits made after this time or on non-business days will
        be processed the next business day.
      </p>
    </div>
  );
}

function UploadComplete() {
  return (
    <div className="w-screen h-screen p-6">
      <div className=" pb-5">
        <div className="sm:flex sm:items-baseline sm:justify-between">
          <div className="sm:w-0 sm:flex-1 text-center">
            <h1
              id="message-heading"
              className="text-base font-semibold text-gray-900"
            >
              Your check has been submitted!
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Return to your desktop to view the status of the deposit.{" "}
              <span className="font-bold text-gray-600">
                Do not destroy or void the check until 90 days after it has been
                deposited into your account.
              </span>{" "}
              You may close this window.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center pt-24">
        <CheckCircle className="w-20 h-20 text-green-700" />
      </div>
    </div>
  );
}

// function CheckImageWithBoundary({ imageUrl, boxes }) {
//   const canvasRef = useRef(null);
//   const imageRef = useRef(null);

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     const context = canvas.getContext("2d");

//     const image = new Image();
//     image.src = imageUrl;
//     image.onload = () => {
//       canvas.width = image.width;
//       canvas.height = image.height;

//       context.drawImage(image, 0, 0);

//       boxes.forEach((box) => {
//         const [
//           { x: tlX, y: tlY },
//           { x: trX, y: trY },
//           { x: brX, y: brY },
//           { x: blX, y: blY },
//         ] = box;

//         // Calculate width and height from corners
//         const width = trX - tlX; // or brX - blX
//         const height = brY - trY; // or blY - tlY
//         context.strokeStyle = "red"; // Color of bounding box
//         context.lineWidth = 2; // Width of bounding box line
//         context.strokeRect(tlX, tlY, width, height); // Draw rectangle
//       });
//     };
//   }, [imageUrl, boxes]);

//   return (
//     <div>
//       <canvas
//         ref={canvasRef}
//         style={{ position: "absolute", top: 0, left: 0 }}
//       />
//       <img
//         ref={imageRef}
//         src={imageUrl}
//         alt="Bounding Box"
//         style={{ display: "none" }}
//       />
//     </div>
//   );
// }
